import React from "react";
import { Tag } from "antd";
import { TIPO_EFECTIVO } from "../../services/topeDelPago";

/**
 * Con qué se paga, dicho de un vistazo y del mismo modo en toda la aplicación.
 *
 * Había tres respuestas distintas a la misma pregunta:
 *
 *   · el desplegable de formas de pago de una factura sólo etiquetaba el
 *     efectivo, así que las demás no decían de qué tipo eran;
 *   · el maestro de formas de pago las etiquetaba todas, pero con dos colores
 *     --verde el efectivo, azul todo lo demás-- y con un verde distinto del
 *     azul claro que usa el desplegable para lo mismo;
 *   · y una forma de pago **sin clasificar** se mostraba como «Efectivo», que
 *     es afirmar lo que no se sabe. Peor aún: es justo lo contrario de lo que
 *     hace la regla de cobro, que ante un tipo desconocido no admite pagar de
 *     más --ver `services/topeDelPago.js`--. La pantalla decía una cosa y el
 *     terminal hacía otra.
 *
 * El tipo importa porque **decide si se puede cobrar de más**: sólo el efectivo
 * admite recibir de sobra y devolver cambio. Que se vea en el desplegable es lo
 * que hace entendible que un método acepte 200.000 sobre una venta de 11.655 y
 * el de al lado no.
 */

/** Un color por tipo, y ninguno repetido: el color es la información. */
export const COLOR_DEL_TIPO = {
  [TIPO_EFECTIVO]: "green",
  Tarjeta: "blue",
  Transferencia: "cyan",
  Cheque: "gold",
  Deposito: "purple",
  Depósito: "purple",
};

/**
 * El color que le toca a un tipo, para quien pinte otra cosa con la misma
 * clave --la sigla del listado de facturas, por ejemplo--. Que el efectivo sea
 * verde en las dos pantallas no es cosmética: es lo que deja reconocerlo sin
 * leer.
 */
export const colorDelTipoDePago = (tipo) =>
  COLOR_DEL_TIPO[String(tipo ?? "").trim()] || "default";

/**
 * @param {object} props
 * @param {string} [props.tipo] La clasificación de la forma de pago.
 * @param {object} [props.style] Espaciado que ponga quien lo coloca.
 */
const TipoDePagoTag = ({ tipo, style }) => {
  const limpio = String(tipo ?? "").trim();

  // Sin clasificar se dice, no se supone. Un método así no admite cobrar de
  // más, y llamarlo «Efectivo» haría esperar justo lo contrario.
  if (!limpio) {
    return (
      <Tag color="default" style={style}>
        Sin clasificar
      </Tag>
    );
  }

  // Un tipo que no está en el mapa se muestra con su propio nombre y en
  // neutro: es preferible a pintarlo del color de otro.
  return (
    <Tag color={COLOR_DEL_TIPO[limpio] || "default"} style={style}>
      {limpio}
    </Tag>
  );
};

export default TipoDePagoTag;
